/**
 * Promo Service
 *
 * Frontend service layer for the Beta BOGO promo campaign.
 * Wraps promo API calls with authenticated fetch and provides
 * stale-while-revalidate caching for status checks.
 *
 * Used by PromoBanner (task 8.2) and promoCapture middleware (task 8.1).
 * Follows existing clientProjectsService pattern.
 */

import { readAdminApiJsonOrThrow } from './adminApiClient';
import { buildApiUrl } from './apiUrl';
import { authenticatedFetch } from '../lib/apiClient';

// --- Types ---

export interface PromoStatusResponse {
  success: true;
  data: {
    hasActivePromo: boolean;
    promoCode: string | null;
    expiresAt: string | null;
    redeemed: boolean;
  };
}

export interface CapturePromoResponse {
  success: true;
  data: {
    captured: boolean;
    reason: string;
  };
}

// --- Cache ---

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: PromoStatusResponse;
  timestamp: number;
}

let statusCache: CacheEntry | null = null;

function isCacheValid(): boolean {
  if (!statusCache) return false;
  return Date.now() - statusCache.timestamp < CACHE_TTL_MS;
}

// --- Internal helpers ---

const PROMO_API_BASE = '/api/admin/promo';

function buildPromoApiUrl(path: string): string {
  return buildApiUrl(`${PROMO_API_BASE}${path}`);
}

// --- Service ---

export const promoService = {
  /**
   * Fetch the current user's promo status.
   *
   * Uses stale-while-revalidate: returns cached data immediately if available,
   * then revalidates in the background. Fresh fetch if no cache exists.
   */
  async getStatus(signal?: AbortSignal): Promise<PromoStatusResponse> {
    // If cache is valid, return it immediately and revalidate in background
    if (isCacheValid() && statusCache) {
      // Background revalidation — fire and forget
      promoService._revalidateStatus();
      return statusCache.data;
    }

    // If cache exists but is stale, return stale data and revalidate
    if (statusCache) {
      promoService._revalidateStatus();
      return statusCache.data;
    }

    // No cache — must fetch fresh
    return promoService._fetchStatus(signal);
  },

  /**
   * Capture a promo code for the authenticated user.
   */
  async capturePromo(
    promoCode: string,
    signal?: AbortSignal,
  ): Promise<CapturePromoResponse> {
    const response = await authenticatedFetch(buildPromoApiUrl('/capture'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ promoCode }),
      signal,
    });

    const result = await readAdminApiJsonOrThrow<CapturePromoResponse>(response);

    // Invalidate status cache after capture so next getStatus() fetches fresh
    statusCache = null;

    return result;
  },

  /**
   * Invalidate the cached promo status.
   * Useful after redemption or other state changes.
   */
  invalidateCache(): void {
    statusCache = null;
  },

  // --- Internal methods ---

  /** @internal Fetch fresh status and update cache. */
  async _fetchStatus(signal?: AbortSignal): Promise<PromoStatusResponse> {
    const response = await authenticatedFetch(buildPromoApiUrl('/status'), {
      method: 'GET',
      signal,
    });

    const result = await readAdminApiJsonOrThrow<PromoStatusResponse>(response);

    statusCache = {
      data: result,
      timestamp: Date.now(),
    };

    return result;
  },

  /** @internal Background revalidation — updates cache silently. */
  _revalidateStatus(): void {
    promoService._fetchStatus().catch(() => {
      // Silently ignore background revalidation errors.
      // The stale cached data remains available.
    });
  },
};
